/**
 * Manager vs Dev — Card Battle Game v2
 * 50 pre-authored rounds, 4-stat engine, emotional reactions, escalation.
 * IIFE, ES5-compatible, no dependencies.
 */
(function () {
    'use strict';

    var root = document.getElementById('card-game');
    if (!root) return;

    // ==================== 50 ROUNDS ====================

    var ROUNDS = [
        // --- Category 1: Deadlines & Delivery (1-6) ---
        {
            id: 1, cat: 'Deadlines & Delivery',
            manager: "I know we just started, but the client wants this 6-month project finished by Friday.",
            options: [
                { text: "We can deliver a static mockup by Friday, but the backend needs the full six months.", arch: 'LOGIC_PUSHBACK', atk: 10 },
                { text: "Sure! Just to confirm \u2014 you mean Friday of November 2027, right?", arch: 'MALICIOUS_COMPLY', atk: 30 },
                { text: "I'll just type 4,000 words a minute and stop sleeping. Should be fine.", arch: 'SAVAGE', atk: 50 }
            ]
        },
        {
            id: 2, cat: 'Deadlines & Delivery',
            manager: "Can we ship this today?",
            options: [
                { text: "We can ship core features only.", arch: 'COMPLY', atk: 10 },
                { text: "Which feature can we drop to make room?", arch: 'REDIRECT', atk: 30 },
                { text: "Shipping today means skipping testing. Your call to make in writing.", arch: 'LOGIC_PUSHBACK', atk: 60 }
            ]
        },
        {
            id: 3, cat: 'Deadlines & Delivery',
            manager: "Why is this delayed?",
            options: [
                { text: "Current ETA is Friday.", arch: 'COMPLY', atk: 10 },
                { text: "Scope changed mid-sprint without a timeline update.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "Initial estimate excluded API issues that were added after sign-off.", arch: 'LOGIC_PUSHBACK', atk: 55 }
            ]
        },
        {
            id: 4, cat: 'Deadlines & Delivery',
            manager: "The client moved the launch date up to tomorrow. Can we hardcode the values for now?",
            options: [
                { text: "Sure, I'll write a script to swap them out after launch.", arch: 'COMPLY', atk: 10 },
                { text: "That's tech debt. I can do it, but it goes in the sprint report.", arch: 'REDIRECT', atk: 30 },
                { text: "No. Hardcoding isn't a feature, it's a lie. We launch when it's ready.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 5, cat: 'Deadlines & Delivery',
            manager: "It's 4:55 PM Friday. I just approved a massive PR. Let's deploy before we head out.",
            options: [
                { text: "Deploying now \u2014 I'll monitor dashboards over the weekend.", arch: 'COMPLY', atk: 10 },
                { text: "Let's schedule it for Monday when we can all monitor it properly.", arch: 'REDIRECT', atk: 45 },
                { text: "I'll just hand you my laptop and you can handle the PagerDuty alerts tomorrow.", arch: 'MALICIOUS_COMPLY', atk: 70 }
            ]
        },
        {
            id: 6, cat: 'Deadlines & Delivery',
            manager: "Let's pivot the feature direction mid-sprint.",
            options: [
                { text: "We can prototype the new direction first.", arch: 'REDIRECT', atk: 20 },
                { text: "What problem are we actually solving now?", arch: 'CLARIFY', atk: 30 },
                { text: "That resets the entire current sprint and breaks our velocity tracking.", arch: 'LOGIC_PUSHBACK', atk: 55 }
            ]
        },
        // --- Category 2: Scope Creep & Absurd Requests (7-15) ---
        {
            id: 7, cat: 'Scope Creep',
            manager: "Can you just add an entire e-commerce store to the app? Shouldn't take more than an hour.",
            options: [
                { text: "E-commerce needs payment gateways and security audits. We scope this for Q3.", arch: 'LOGIC_PUSHBACK', atk: 10 },
                { text: "Sure, I'll drag and drop the 'Make Store' file into the codebase. Done.", arch: 'MALICIOUS_COMPLY', atk: 35 },
                { text: "I can do it in 30 minutes if you don't mind customers' cards on Twitter.", arch: 'SAVAGE', atk: 70 }
            ]
        },
        {
            id: 8, cat: 'Scope Creep',
            manager: "The investors are coming. Add Blockchain and AI to the Forgot Password screen.",
            options: [
                { text: "Those technologies don't handle email verification. It adds latency with no benefit.", arch: 'LOGIC_PUSHBACK', atk: 15 },
                { text: "I can add a button that says 'Powered by AI' \u2014 it'll still just send an email.", arch: 'MALICIOUS_COMPLY', atk: 35 },
                { text: "Only if we also rewrite the database in HTML to make it Web3 compliant.", arch: 'SAVAGE', atk: 60 }
            ]
        },
        {
            id: 9, cat: 'Scope Creep',
            manager: "Make it viral.",
            options: [
                { text: "We can add a referral loop to the onboarding flow.", arch: 'REDIRECT', atk: 20 },
                { text: "Define a viral metric first \u2014 shares, signups, or session length?", arch: 'CLARIFY', atk: 30 },
                { text: "Virality needs a marketing push. Code alone can't manufacture that.", arch: 'LOGIC_PUSHBACK', atk: 50 }
            ]
        },
        {
            id: 10, cat: 'Scope Creep',
            manager: "Add AI to it.",
            options: [
                { text: "We can add AI-generated labels as a start.", arch: 'COMPLY', atk: 15 },
                { text: "For which specific workflow?", arch: 'CLARIFY', atk: 30 },
                { text: "The data isn't clean enough yet for any model to be useful.", arch: 'LOGIC_PUSHBACK', atk: 55 }
            ]
        },
        {
            id: 11, cat: 'Scope Creep',
            manager: "I know we're building a To-Do list, but can we slip in a blockchain integration? Investors love that.",
            options: [
                { text: "I'll look into some APIs that might work.", arch: 'COMPLY', atk: 10 },
                { text: "A To-Do list doesn't need a ledger. Do you want users or buzzwords?", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "I'm not burning gas fees to save a grocery list. Next question.", arch: 'SAVAGE', atk: 60 }
            ]
        },
        {
            id: 12, cat: 'Scope Creep',
            manager: "Can you make the app run 100x faster, but absolutely do not touch the legacy code.",
            options: [
                { text: "I'll order a server with 100x the processing power. Approve the $50k invoice?", arch: 'MALICIOUS_COMPLY', atk: 40 },
                { text: "The bottlenecks live inside the legacy code. You can't have both.", arch: 'LOGIC_PUSHBACK', atk: 60 },
                { text: "I will light a candle, pray to the machine spirit, and sprinkle holy water on the servers.", arch: 'SAVAGE', atk: 65 }
            ]
        },
        {
            id: 13, cat: 'Scope Creep',
            manager: "We need to make the app more 'premium'. Make it pop.",
            options: [
                { text: "We can enhance animations and polish the UI.", arch: 'COMPLY', atk: 10 },
                { text: "Do you mean color contrast, layout, or motion? Can you share a reference?", arch: 'CLARIFY', atk: 25 },
                { text: "'Pop' isn't a ticket. Send me a Figma file or it stays the same.", arch: 'BRUTAL_TRUTH', atk: 55 }
            ]
        },
        {
            id: 14, cat: 'Scope Creep',
            manager: "I saw a feature on a competitor's site. Just copy it \u2014 don't worry about patents.",
            options: [
                { text: "I'll see what I can replicate safely.", arch: 'COMPLY', atk: 10 },
                { text: "We need legal to check this before I touch it.", arch: 'REDIRECT', atk: 30 },
                { text: "I'm not building a lawsuit into the codebase. Find another way.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 15, cat: 'Scope Creep',
            manager: "Can we do a VR version of the website?",
            options: [
                { text: "I'll look into WebGL as a starting point.", arch: 'COMPLY', atk: 10 },
                { text: "Nobody has a VR headset for browsing websites.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "That's a solution looking for a problem.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        // --- Category 3: Quality, Testing & Security (16-21) ---
        {
            id: 16, cat: 'Quality & Security',
            manager: "We don't have time for QA. Just push it to production. Real men test in prod.",
            options: [
                { text: "Skipping QA costs ten times more in dev hours when users find the critical bugs.", arch: 'LOGIC_PUSHBACK', atk: 15 },
                { text: "Ah yes \u2014 Scream-Driven Development. I love when clients find our bugs for us.", arch: 'MALICIOUS_COMPLY', atk: 45 },
                { text: "Already pushed it. The server is currently on fire. Good luck.", arch: 'SAVAGE', atk: 75 }
            ]
        },
        {
            id: 17, cat: 'Quality & Security',
            manager: "Can we store user passwords in plain text? Makes debugging easier.",
            options: [
                { text: "I'll encrypt them but keep a local debug key for testing only.", arch: 'REDIRECT', atk: 10 },
                { text: "That's a compliance violation. I won't put us at risk of a lawsuit.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "Do you want to be on the news for a data breach?", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 18, cat: 'Quality & Security',
            manager: "A user says the app crashes when they click a button. Just put a try-catch around it.",
            options: [
                { text: "Okay, I'll suppress the visible error for now.", arch: 'COMPLY', atk: 10 },
                { text: "That hides the symptom. I need to fix the root cause.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "A try-catch is a band-aid on a bullet hole. I'm debugging it properly.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 19, cat: 'Quality & Security',
            manager: "We launched! But login is broken. How did this pass QA?",
            options: [
                { text: "I'll investigate environment differences immediately.", arch: 'COMPLY', atk: 10 },
                { text: "QA passed the build you approved. Do you want me to fix it or find who to blame?", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "You skipped the testing phase to meet the deadline. This is the cost.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 20, cat: 'Quality & Security',
            manager: "Security review is optional this sprint. We're in a hurry.",
            options: [
                { text: "I'll do a quick self-review instead.", arch: 'COMPLY', atk: 10 },
                { text: "A breach after launch costs exponentially more than a review now.", arch: 'LOGIC_PUSHBACK', atk: 40 },
                { text: "I won't ship unreviewed auth code. This isn't optional.", arch: 'BRUTAL_TRUTH', atk: 70 }
            ]
        },
        {
            id: 21, cat: 'Quality & Security',
            manager: "Install GPS tracking on employee laptops to monitor productivity.",
            options: [
                { text: "I'll configure the MDM software.", arch: 'COMPLY', atk: 10 },
                { text: "That's a privacy violation. Morale will tank significantly.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "I'm not building spyware for my own team.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        // --- Category 4: Infrastructure & Architecture (22-28) ---
        {
            id: 22, cat: 'Infrastructure',
            manager: "AWS is too expensive. Can we host the enterprise database on my old gaming laptop?",
            options: [
                { text: "A personal laptop can't meet the redundancy or security protocols required.", arch: 'LOGIC_PUSHBACK', atk: 20 },
                { text: "Only if your mom promises not to unplug the router while vacuuming.", arch: 'MALICIOUS_COMPLY', atk: 50 },
                { text: "Let's use Excel as our database while we're at it.", arch: 'SAVAGE', atk: 80 }
            ]
        },
        {
            id: 23, cat: 'Infrastructure',
            manager: "I know we've been building in Python for 2 years, but I read Rust is faster. Rewrite it.",
            options: [
                { text: "A full rewrite halts all feature development for at least a year.", arch: 'LOGIC_PUSHBACK', atk: 25 },
                { text: "I'll run the Python through Google Translate into Rust. Flawless.", arch: 'MALICIOUS_COMPLY', atk: 50 },
                { text: "I'm going to need therapy, and you're going to need a lot more budget.", arch: 'SAVAGE', atk: 90 }
            ]
        },
        {
            id: 24, cat: 'Infrastructure',
            manager: "Stop using Git. Just zip your files and email them to me at the end of the day.",
            options: [
                { text: "Git is the industry standard. Zip files cause lost work and merge conflicts.", arch: 'LOGIC_PUSHBACK', atk: 25 },
                { text: "Prepare your inbox for a 4GB node_modules zip file every day at 5 PM.", arch: 'MALICIOUS_COMPLY', atk: 60 },
                { text: "Why stop there? Let's just fax the code to each other.", arch: 'SAVAGE', atk: 90 }
            ]
        },
        {
            id: 25, cat: 'Infrastructure',
            manager: "Can we switch from SQL to NoSQL mid-project? It's trendier.",
            options: [
                { text: "I'll start migrating the schema.", arch: 'COMPLY', atk: 10 },
                { text: "That's a full backend rewrite. We need to finish the current sprint first.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "We don't refactor a flying plane. Stick to the plan.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 26, cat: 'Infrastructure',
            manager: "My cousin has a server in his basement. Let's host there to save money.",
            options: [
                { text: "I'll check his uptime specs.", arch: 'COMPLY', atk: 10 },
                { text: "We need enterprise SLAs, not a home internet connection.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "When his power goes out, our business stops. No.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 27, cat: 'Infrastructure',
            manager: "The third-party payment API went down globally. Fix it immediately!",
            options: [
                { text: "We don't control their servers. We wait for them to resolve the outage.", arch: 'LOGIC_PUSHBACK', atk: 10 },
                { text: "I'll drive over to their headquarters and personally plug the server back in.", arch: 'MALICIOUS_COMPLY', atk: 35 },
                { text: "Let me just hack into Stripe's mainframe and rewrite their infrastructure real quick.", arch: 'SAVAGE', atk: 80 }
            ]
        },
        {
            id: 28, cat: 'Infrastructure',
            manager: "Can we host the entire app on a free tier forever? We're a startup.",
            options: [
                { text: "I'll try to fit within the free limits.", arch: 'COMPLY', atk: 10 },
                { text: "Free tiers have hard ceilings. We'll crash when we start scaling.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "You get what you pay for. Do you want reliability or free?", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        // --- Category 5: Meetings, Process & Workflow (29-35) ---
        {
            id: 29, cat: 'Process & Meetings',
            manager: "I've scheduled a mandatory 3-hour sync every morning to track how fast you're coding.",
            options: [
                { text: "A 3-hour meeting removes 3 hours of coding time. How about a 15-minute standup?", arch: 'LOGIC_PUSHBACK', atk: 20 },
                { text: "Let's make it 8 hours \u2014 I'll share my screen and narrate every keystroke live.", arch: 'MALICIOUS_COMPLY', atk: 40 },
                { text: "If I attend a 3-hour meeting every morning, the only thing I'll be developing is deep vein thrombosis.", arch: 'SAVAGE', atk: 65 }
            ]
        },
        {
            id: 30, cat: 'Process & Meetings',
            manager: "We need a 1-hour sync to discuss why the last 1-hour sync was unproductive.",
            options: [
                { text: "I'll put it on my calendar.", arch: 'COMPLY', atk: 10 },
                { text: "Can we send a summary email instead? I have 4 hours of coding blocked.", arch: 'REDIRECT', atk: 30 },
                { text: "I'll attend, but I'll be coding on a second screen to meet my actual deadline.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 31, cat: 'Process & Meetings',
            manager: "Give me an exact hour estimate for this vague requirement.",
            options: [
                { text: "I'll guess 40 hours.", arch: 'COMPLY', atk: 10 },
                { text: "I can give a range, but not a precise number until it's properly scoped.", arch: 'CLARIFY', atk: 30 },
                { text: "Estimating vagueness is just guessing. Define the work first.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 32, cat: 'Process & Meetings',
            manager: "Your Jira tickets are too detailed. Just write 'Fix stuff'.",
            options: [
                { text: "I'll shorten the descriptions.", arch: 'COMPLY', atk: 10 },
                { text: "Details help track progress and protect everyone when things slip.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "Then don't ask me why things take longer than expected.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 33, cat: 'Process & Meetings',
            manager: "Why is your Slack status 'Away' during work hours?",
            options: [
                { text: "Sorry, I forgot to update it.", arch: 'COMPLY', atk: 10 },
                { text: "I was in deep work mode. Status doesn't reflect output.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "I'm coding, not chatting. My commits show my work.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 34, cat: 'Process & Meetings',
            manager: "You need to reply to all emails within 5 minutes.",
            options: [
                { text: "I'll keep my inbox open.", arch: 'COMPLY', atk: 10 },
                { text: "Constant context-switching kills deep work and slows actual delivery.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "I'm not an instant messenger. I'll reply when I can.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 35, cat: 'Process & Meetings',
            manager: "The site is down. Did you push something? Rollback immediately!",
            options: [
                { text: "Rolling back now, I'll review logs after.", arch: 'COMPLY', atk: 10 },
                { text: "My last commit was 3 days ago. Check the deploy logs before pointing at me.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "Panic doesn't fix servers. Give me 5 minutes to read the logs properly.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        // --- Category 6: HR, Workplace & Personal Boundaries (36-44) ---
        {
            id: 36, cat: 'Workplace & HR',
            manager: "Can you work this weekend? It's just this once, I promise.",
            options: [
                { text: "Sure, I'll be online.", arch: 'COMPLY', atk: 10 },
                { text: "I can do a few hours if we agree on comp time upfront.", arch: 'REDIRECT', atk: 30 },
                { text: "This is the fourth 'just this once' since October. I need this tracked and compensated.", arch: 'BRUTAL_TRUTH', atk: 70 }
            ]
        },
        {
            id: 37, cat: 'Workplace & HR',
            manager: "Hey, it's Saturday, but a critical issue came up. Can you hop on a call?",
            options: [
                { text: "Sure, sending the link now.", arch: 'COMPLY', atk: 10 },
                { text: "I'm offline today. I'll handle it first thing Monday morning.", arch: 'REDIRECT', atk: 30 },
                { text: "My contract says 9-to-5. See you Monday.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 38, cat: 'Workplace & HR',
            manager: "Team building escape room this weekend. It's mandatory.",
            options: [
                { text: "I'll register.", arch: 'COMPLY', atk: 10 },
                { text: "Weekends are off. Can we do this during work hours instead?", arch: 'REDIRECT', atk: 30 },
                { text: "Mandatory fun on personal time isn't okay. I'm out.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 39, cat: 'Workplace & HR',
            manager: "Everyone's in the office 5 days a week now. Collaboration is key.",
            options: [
                { text: "I'll clear my desk.", arch: 'COMPLY', atk: 10 },
                { text: "My productivity data says otherwise. Can we discuss a hybrid model?", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "I'll come in, but I'll be on headphones coding all day anyway.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 40, cat: 'Workplace & HR',
            manager: "We can't pay overtime, but you can take comp time later.",
            options: [
                { text: "Okay, I'll track the hours.", arch: 'COMPLY', atk: 10 },
                { text: "Comp time is hard to schedule around sprints. I'd prefer overtime pay.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "My bills want cash, not time off.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 41, cat: 'Workplace & HR',
            manager: "Your vacation request is still pending. We're too busy.",
            options: [
                { text: "I'll cancel the trip.", arch: 'COMPLY', atk: 10 },
                { text: "I booked this months ago. I need the break.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "Burnout costs more than my vacation. Approve it.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 42, cat: 'Workplace & HR',
            manager: "For your review, please rate your own performance out of 10.",
            options: [
                { text: "I'll give myself a 7.", arch: 'COMPLY', atk: 10 },
                { text: "I'll list my achievements, but numbers are subjective.", arch: 'REDIRECT', atk: 30 },
                { text: "That's your job to evaluate. I'm not grading myself.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 43, cat: 'Workplace & HR',
            manager: "When can we expect you to be promoted to Senior?",
            options: [
                { text: "Whenever you think is the right time.", arch: 'COMPLY', atk: 10 },
                { text: "I've met the criteria. Let's review the promotion path together.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "I'm already doing the work. The title and pay should match.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 44, cat: 'Workplace & HR',
            manager: "We heard you're looking elsewhere. Here's a counter-offer.",
            options: [
                { text: "Tell me more about the offer.", arch: 'COMPLY', atk: 10 },
                { text: "It's not just about money. It's about the culture here.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "Too late. I'm already gone.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        // --- Category 7: Legendary Rounds (45-50) ---
        {
            id: 45, cat: 'Legendary',
            manager: "I was poking around the admin panel and accidentally deleted the production database. Fix it.",
            options: [
                { text: "Taking the site into maintenance mode and restoring from nightly backups now.", arch: 'COMPLY', atk: 25 },
                { text: "You mean the database you explicitly said we didn't need to back up to 'save server space'?", arch: 'BRUTAL_TRUTH', atk: 60 },
                { text: "Update your LinkedIn. We're both getting fired.", arch: 'SAVAGE', atk: 100 }
            ]
        },
        {
            id: 46, cat: 'Legendary',
            manager: "ChatGPT can write code now. Do we still need you full-time?",
            options: [
                { text: "I'll use AI to speed up my workflow.", arch: 'COMPLY', atk: 10 },
                { text: "AI writes code. I engineer solutions. There's a difference.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "Sure, let's ask AI to fix the server when it crashes at 3 AM.", arch: 'BRUTAL_TRUTH', atk: 60 }
            ]
        },
        {
            id: 47, cat: 'Legendary',
            manager: "I told the client the app works entirely offline. It's a cloud-based video streaming service.",
            options: [
                { text: "We can't stream video without internet. We need to manage expectations.", arch: 'LOGIC_PUSHBACK', atk: 20 },
                { text: "I'll download the entire internet onto their hard drive during installation.", arch: 'MALICIOUS_COMPLY', atk: 55 },
                { text: "I'll hand-deliver a stack of flipbooks and a flashlight to the client's house.", arch: 'SAVAGE', atk: 85 }
            ]
        },
        {
            id: 48, cat: 'Legendary',
            manager: "Users are complaining passwords are too strict. Just remove the password requirement entirely.",
            options: [
                { text: "We can simplify the rules or implement SSO, but auth can't be removed.", arch: 'LOGIC_PUSHBACK', atk: 25 },
                { text: "Done. The new password is 'password' for everyone. UX is up 100%.", arch: 'MALICIOUS_COMPLY', atk: 55 },
                { text: "Why stop there? Let's just email everyone's data to them daily so they never log in.", arch: 'SAVAGE', atk: 85 }
            ]
        },
        {
            id: 49, cat: 'Legendary',
            manager: "Hey, could you help me hack my ex's Instagram? It's basically the same thing you do here.",
            options: [
                { text: "I'm a software engineer, not a hacker. And that's highly illegal.", arch: 'LOGIC_PUSHBACK', atk: 30 },
                { text: "Put that request in a formal Jira ticket so HR can review it.", arch: 'MALICIOUS_COMPLY', atk: 60 },
                { text: "Sure, right after I help a Nigerian Prince transfer $50M to my account.", arch: 'SAVAGE', atk: 85 }
            ]
        },
        {
            id: 50, cat: 'Legendary',
            manager: "I shared your personal GitHub with the client. They love you \u2014 can you freelance for them on the side?",
            options: [
                { text: "That's flattering. Let me think about it and get back to you.", arch: 'COMPLY', atk: 10 },
                { text: "I'd need to check my contract and get formal approval before anything.", arch: 'REDIRECT', atk: 40 },
                { text: "You shared my personal accounts without consent. That likely violates my employment contract. HR needs to be involved.", arch: 'BRUTAL_TRUTH', atk: 100 }
            ]
        }
    ];

    // ==================== ARCHETYPE CONFIG ====================

    var ARCHETYPES = {
        COMPLY:          { label: 'Go Along',    color: '#6b7280' },
        LOGIC_PUSHBACK:  { label: 'Push Back',   color: '#3b82f6' },
        CLARIFY:         { label: 'Question It',  color: '#06b6d4' },
        REDIRECT:        { label: 'Redirect',    color: '#8b5cf6' },
        BRUTAL_TRUTH:    { label: 'Brutal Truth', color: '#f97316' },
        MALICIOUS_COMPLY:{ label: 'Malicious Compliance', color: '#eab308' },
        SAVAGE:          { label: 'Savage',       color: '#ef4444' }
    };

    // Base stat effects per archetype
    var BASE_EFFECTS = {
        COMPLY:          { ME:  3, MP:  3, TM: -3, DR: -2 },
        LOGIC_PUSHBACK:  { ME: -3, MP: -3, TM:  2, DR:  3 },
        CLARIFY:         { ME: -1, MP:  2, TM:  1, DR:  2 },
        REDIRECT:        { ME: -2, MP:  1, TM:  2, DR:  2 },
        BRUTAL_TRUTH:    { ME: -5, MP: -5, TM:  3, DR:  4 },
        MALICIOUS_COMPLY:{ ME:  2, MP: -4, TM:  2, DR:  1 },
        SAVAGE:          { ME: -8, MP: -8, TM:  2, DR:  5 }
    };

    // Bonus effects based on Manager Ego tier
    var EGO_BONUS = {
        high: { // ME > 75
            COMPLY:          { ME:  2, TM: -2 },
            LOGIC_PUSHBACK:  { MP: -5, DR:  5 },
            CLARIFY:         { MP: -3, DR:  3 },
            REDIRECT:        { MP: -3, DR:  3 },
            BRUTAL_TRUTH:    { MP: -10, DR: -5 },
            MALICIOUS_COMPLY:{ ME:  3, MP: -5 },
            SAVAGE:          { MP: -12, DR: -8 }
        },
        normal: { // ME 40-75
            COMPLY:          { MP:  2 },
            LOGIC_PUSHBACK:  { DR:  2 },
            CLARIFY:         { DR:  2, MP:  2 },
            REDIRECT:        { MP:  2, DR:  2 },
            BRUTAL_TRUTH:    { DR:  3, MP: -3 },
            MALICIOUS_COMPLY:{ DR:  2 },
            SAVAGE:          { DR:  3, MP: -5 }
        },
        low: { // ME < 40
            COMPLY:          { ME:  3, MP:  3 },
            LOGIC_PUSHBACK:  { DR:  5, TM:  3 },
            CLARIFY:         { DR:  3, TM:  2 },
            REDIRECT:        { DR:  3, TM:  2 },
            BRUTAL_TRUTH:    { DR:  8 },
            MALICIOUS_COMPLY:{ DR:  5 },
            SAVAGE:          { DR: 10, TM:  3 }
        }
    };

    // ==================== ESCALATION EVENTS ====================

    var ESCALATIONS = [
        { text: "Emergency all-hands meeting called!", effects: { MP: -10, TM: -5 } },
        { text: "CEO drops by the standup unannounced.", effects: { ME: 15, MP: -10 } },
        { text: "Production outage! All hands on deck.", effects: { TM: -10, DR: -5 } },
        { text: "Dev gets public Slack praise from the VP.", effects: { DR: 10, ME: -5 } },
        { text: "Budget cuts announced across the board.", effects: { TM: -10, MP: -10 } },
        { text: "Competitor launched a similar feature first.", effects: { ME: 10, MP: -10 } },
        { text: "A senior team member just quit.", effects: { TM: -15, DR: 5 } },
        { text: "Client sends a furious email to the CEO.", effects: { ME: 10, TM: -5 } },
        { text: "Sprint review went surprisingly well!", effects: { TM: 10, DR: 5 } },
        { text: "Manager gets tough feedback from their boss.", effects: { ME: -10, MP: 10 } }
    ];

    // ==================== REACTION TEXT POOLS ====================

    var REACTIONS = {
        inflated: [ // ME > 75
            "I know what I'm doing. I've been in management for years.",
            "That's cute, but I have a meeting to run.",
            "I'll take that into consideration.",
            "You're overthinking this.",
            "Let's not get emotional about code.",
            "I appreciate your... enthusiasm."
        ],
        confident: [ // ME 60-75
            "Noted. Let's move on.",
            "Fair point, but we have deadlines.",
            "I hear you, but the business needs come first.",
            "Let's find a middle ground.",
            "I'll add it to the backlog.",
            "Interesting perspective."
        ],
        neutral: [ // ME 40-60
            "OK, that's reasonable.",
            "Good point. Let me think about it.",
            "You might be right about this one.",
            "I hadn't considered that angle.",
            "Let's discuss this further.",
            "Fair enough."
        ],
        shaken: [ // ME 20-40
            "...You might have a point.",
            "Let me... reconsider.",
            "I wasn't thinking about it that way.",
            "Maybe I should run this by someone else.",
            "I'm starting to see the issue.",
            "Perhaps we should slow down."
        ],
        broken: [ // ME < 20
            "I... okay. Whatever you think is best.",
            "Maybe I'm not cut out for this.",
            "You're right. About everything.",
            "Please don't leave the team.",
            "I'll just approve whatever you suggest.",
            "I need to rethink my whole approach."
        ]
    };

    // ==================== PIXEL ART ====================

    var PX = 4;

    var DEV_SPRITE = {
        palette: ['transparent','#f2c9a0','#2d2d2d','#3b82f6','#1e3a8a','#334155','#64748b'],
        grid: [
            [0,0,0,2,2,2,2,0,0,0],
            [0,0,2,2,2,2,2,2,0,0],
            [0,0,1,1,1,1,1,1,0,0],
            [0,0,1,2,1,1,2,1,0,0],
            [0,0,1,1,1,1,1,1,0,0],
            [0,0,0,1,1,1,1,0,0,0],
            [0,0,3,3,3,3,3,3,0,0],
            [0,3,3,3,3,3,3,3,3,0],
            [0,1,3,3,3,3,3,3,1,0],
            [0,0,1,6,6,6,6,1,0,0],
            [0,0,0,6,6,6,6,0,0,0],
            [0,0,0,4,4,4,4,0,0,0],
            [0,0,0,4,0,0,4,0,0,0],
            [0,0,0,5,0,0,5,0,0,0]
        ]
    };

    var MGR_SPRITE = {
        palette: ['transparent','#f2c9a0','#1a1a2e','#475569','#334155','#1e293b','#ef4444'],
        grid: [
            [0,0,0,2,2,2,2,0,0,0],
            [0,0,2,2,2,2,2,2,0,0],
            [0,0,1,1,1,1,1,1,0,0],
            [0,0,1,2,1,1,2,1,0,0],
            [0,0,1,1,1,1,1,1,0,0],
            [0,0,0,1,1,1,1,0,0,0],
            [0,0,3,3,6,6,3,3,0,0],
            [0,3,3,3,6,6,3,3,3,0],
            [0,1,3,3,3,3,3,3,1,0],
            [0,0,1,3,3,3,3,1,0,0],
            [0,0,0,4,4,4,4,0,0,0],
            [0,0,0,4,4,4,4,0,0,0],
            [0,0,0,4,0,0,4,0,0,0],
            [0,0,0,5,0,0,5,0,0,0]
        ]
    };

    function buildSVG(sprite, scale) {
        var s = scale || 1;
        var px = PX * s;
        var cols = sprite.grid[0].length;
        var rows = sprite.grid.length;
        var w = cols * px;
        var h = rows * px;
        var rects = [];

        for (var r = 0; r < rows; r++) {
            for (var c = 0; c < cols; c++) {
                var idx = sprite.grid[r][c];
                if (idx === 0) continue;
                rects.push(
                    '<rect x="' + (c * px) + '" y="' + (r * px) +
                    '" width="' + px + '" height="' + px +
                    '" fill="' + sprite.palette[idx] + '"/>'
                );
            }
        }

        return '<svg width="' + w + '" height="' + h +
               '" viewBox="0 0 ' + w + ' ' + h +
               '" xmlns="http://www.w3.org/2000/svg" style="image-rendering:pixelated">' +
               rects.join('') + '</svg>';
    }

    // ==================== GAME STATE ====================

    var state = {
        screen: 'title',
        ME: 60,
        MP: 70,
        TM: 75,
        DR: 50,
        round: 0,
        maxRounds: 10,
        deck: [],
        currentRound: null,
        chosenOption: null,
        reaction: '',
        reactionTier: '',
        escalation: null,
        chaosMode: false,
        chaosTriggered: false,
        animating: false,
        statChanges: null,
        endCondition: null,
        seniorCardAvailable: false,
        seniorCardUsed: false
    };

    // ==================== UTILITIES ====================

    function shuffle(arr) {
        var a = arr.slice();
        for (var i = a.length - 1; i > 0; i--) {
            var j = Math.floor(Math.random() * (i + 1));
            var tmp = a[i]; a[i] = a[j]; a[j] = tmp;
        }
        return a;
    }

    function clamp(val, min, max) {
        return Math.max(min, Math.min(max, val));
    }

    function randomFrom(arr) {
        return arr[Math.floor(Math.random() * arr.length)];
    }

    function statColor(key) {
        switch (key) {
            case 'ME': return '#ef4444';
            case 'MP': return '#f59e0b';
            case 'TM': return '#3b82f6';
            case 'DR': return '#22c55e';
            default:   return '#888';
        }
    }

    function statLabel(key) {
        switch (key) {
            case 'ME': return 'Manager Ego';
            case 'MP': return 'Manager Patience';
            case 'TM': return 'Team Morale';
            case 'DR': return 'Dev Reputation';
            default:   return key;
        }
    }

    function getEgoTier(me) {
        if (me > 75) return 'high';
        if (me >= 40) return 'normal';
        return 'low';
    }

    function getReactionTier(me) {
        if (me > 75) return 'inflated';
        if (me > 60) return 'confident';
        if (me >= 40) return 'neutral';
        if (me >= 20) return 'shaken';
        return 'broken';
    }

    function catColor(cat) {
        var colors = {
            'Deadlines & Delivery': '#dc2626',
            'Scope Creep': '#f97316',
            'Quality & Security': '#8b5cf6',
            'Infrastructure': '#06b6d4',
            'Process & Meetings': '#6366f1',
            'Workplace & HR': '#ec4899',
            'Legendary': '#eab308'
        };
        return colors[cat] || '#666';
    }

    // ==================== STAT CALCULATION ====================

    function calculateEffects(option) {
        var arch = option.arch;
        var atk = option.atk;
        var base = BASE_EFFECTS[arch];
        var egoTier = getEgoTier(state.ME);
        var bonus = EGO_BONUS[egoTier][arch] || {};

        // ATK scaling: mild multiplier
        var scale = atk / 40; // ATK 10 = 0.25, ATK 30 = 0.75, ATK 60 = 1.5, ATK 100 = 2.5
        scale = Math.max(0.3, Math.min(scale, 2.5));

        var changes = {};
        var keys = ['ME', 'MP', 'TM', 'DR'];
        for (var i = 0; i < keys.length; i++) {
            var k = keys[i];
            var baseVal = base[k] || 0;
            var bonusVal = bonus[k] || 0;
            changes[k] = Math.round(baseVal * scale) + bonusVal;
        }

        // Chaos mode: double all negative effects on dev-side stats
        if (state.chaosMode) {
            if (changes.TM < 0) changes.TM *= 2;
            if (changes.DR < 0) changes.DR *= 2;
        }

        return changes;
    }

    function applyEffects(changes) {
        state.ME = clamp(state.ME + (changes.ME || 0), 0, 100);
        state.MP = clamp(state.MP + (changes.MP || 0), 0, 100);
        state.TM = clamp(state.TM + (changes.TM || 0), 0, 100);
        state.DR = clamp(state.DR + (changes.DR || 0), 0, 100);
    }

    function checkWinLoss() {
        if (state.DR >= 80) return { winner: 'dev', reason: 'Legendary Dev \u2014 promoted to Staff Engineer!' };
        if (state.ME <= 10) return { winner: 'dev', reason: 'Manager Broken \u2014 they quit management!' };
        if (state.TM <= 15) return { winner: 'mgr', reason: 'Team Collapse \u2014 everyone quits!' };
        if (state.DR <= 5) return { winner: 'mgr', reason: 'Career Over \u2014 PIP\'d into oblivion!' };
        return null;
    }

    function checkChaos() {
        if (!state.chaosTriggered && state.MP <= 10) {
            state.chaosMode = true;
            state.chaosTriggered = true;
            return true;
        }
        return false;
    }

    function checkSeniorCard() {
        if (!state.seniorCardUsed && state.DR > 70) {
            state.seniorCardAvailable = true;
        }
    }

    function getGrade() {
        var score = state.DR + state.TM - state.ME;
        if (score >= 140) return 'S';
        if (score >= 110) return 'A';
        if (score >= 80)  return 'B';
        if (score >= 50)  return 'C';
        return 'F';
    }

    function getGradeNarrative(grade) {
        switch (grade) {
            case 'S': return 'You are the mythical 10x developer. The manager now asks YOU for career advice.';
            case 'A': return 'Stellar performance. Your code speaks louder than any meeting ever could.';
            case 'B': return 'Solid work. You survived the sprint with your dignity mostly intact.';
            case 'C': return 'It was rough, but you made it. Barely. Your Slack status is permanently "Away."';
            case 'F': return 'The manager won. You\'re now writing Jira tickets about writing Jira tickets.';
            default:  return '';
        }
    }

    // ==================== RENDERING HELPERS ====================

    function statBarHTML(key, val, change) {
        var pct = clamp(val, 0, 100);
        var color = statColor(key);
        var changeHTML = '';
        if (change !== undefined && change !== 0) {
            var cls = change > 0 ? 'game-stat__change--pos' : 'game-stat__change--neg';
            changeHTML = '<span class="game-stat__change ' + cls + '">' + (change > 0 ? '+' : '') + change + '</span>';
        }
        return '<div class="game-stat">' +
            '<div class="game-stat__header">' +
                '<span class="game-stat__label">' + statLabel(key) + '</span>' +
                '<span class="game-stat__value">' + pct + changeHTML + '</span>' +
            '</div>' +
            '<div class="game-stat__track">' +
                '<div class="game-stat__fill" style="width:' + pct + '%;background:' + color + '"></div>' +
            '</div>' +
        '</div>';
    }

    function archBadge(arch) {
        var cfg = ARCHETYPES[arch];
        if (!cfg) return '';
        return '<span class="game-arch" style="background:' + cfg.color + '">' + cfg.label + '</span>';
    }

    function catBadge(cat) {
        return '<span class="game-cat" style="background:' + catColor(cat) + '">' + cat + '</span>';
    }

    // ==================== SCREEN: TITLE ====================

    function renderTitle() {
        root.innerHTML =
            '<div class="game-title">' +
                '<div class="game-title__icon">' +
                    buildSVG(DEV_SPRITE, 2) +
                    '<span class="game-title__vs">VS</span>' +
                    buildSVG(MGR_SPRITE, 2) +
                '</div>' +
                '<h1 class="game-title__heading">MANAGER vs DEV</h1>' +
                '<p class="game-title__sub">Navigate corporate chaos. Guard your reputation. Survive the sprint.</p>' +
                '<div class="game-title__rules">' +
                    '<p><strong>10 rounds.</strong> Your manager throws absurd requests at you. Pick your response wisely.</p>' +
                    '<p>Each response has a hidden <strong>archetype</strong> that affects 4 stats: Manager Ego, Manager Patience, Team Morale, and your Dev Reputation.</p>' +
                    '<p><strong>Win:</strong> Reach 80 Dev Reputation or break the Manager\'s Ego below 10.</p>' +
                    '<p><strong>Lose:</strong> Team Morale drops below 15 or your Reputation hits 5.</p>' +
                    '<p>Watch out for <strong>Escalation Events</strong> every 3 rounds!</p>' +
                '</div>' +
                '<button class="game-btn game-btn--start" id="game-start">START GAME</button>' +
            '</div>';

        document.getElementById('game-start').addEventListener('click', startGame);
    }

    // ==================== SCREEN: BATTLE ====================

    function renderBattle() {
        var round = state.currentRound;
        var changes = state.statChanges;

        // Stats bar
        var statsHTML =
            '<div class="game-stats' + (state.chaosMode ? ' game-stats--chaos' : '') + '">' +
                statBarHTML('ME', state.ME, changes ? changes.ME : undefined) +
                statBarHTML('MP', state.MP, changes ? changes.MP : undefined) +
                statBarHTML('TM', state.TM, changes ? changes.TM : undefined) +
                statBarHTML('DR', state.DR, changes ? changes.DR : undefined) +
            '</div>';

        // Round + chaos indicator
        var roundHTML =
            '<div class="game-round">' +
                (state.chaosMode ? '<span class="game-round__chaos">CHAOS MODE</span>' : '') +
                '<span>Round ' + state.round + ' / ' + state.maxRounds + '</span>' +
            '</div>';

        // Manager area
        var mgrHTML =
            '<div class="game-manager">' +
                '<div class="game-manager__sprite">' + buildSVG(MGR_SPRITE, 2) + '</div>' +
                (state.reaction
                    ? '<div class="game-manager__bubble game-manager__bubble--' + state.reactionTier + '">' +
                        '<p>' + state.reaction + '</p>' +
                      '</div>'
                    : '') +
            '</div>';

        // Prompt card
        var promptHTML = '';
        if (round) {
            promptHTML =
                '<div class="game-prompt' + (state.chaosMode ? ' game-prompt--chaos' : '') + '">' +
                    catBadge(round.cat) +
                    '<p class="game-prompt__text">\u201C' + round.manager + '\u201D</p>' +
                '</div>';
        }

        // Options
        var optionsHTML = '';
        if (round && !state.animating) {
            optionsHTML = '<div class="game-options">';
            for (var i = 0; i < round.options.length; i++) {
                var opt = round.options[i];
                optionsHTML +=
                    '<button class="game-option" data-index="' + i + '">' +
                        archBadge(opt.arch) +
                        '<p class="game-option__text">\u201C' + opt.text + '\u201D</p>' +
                    '</button>';
            }
            optionsHTML += '</div>';
        } else if (state.animating && state.chosenOption) {
            // Show chosen option highlighted
            optionsHTML =
                '<div class="game-options game-options--resolved">' +
                    '<div class="game-option game-option--chosen">' +
                        archBadge(state.chosenOption.arch) +
                        '<p class="game-option__text">\u201C' + state.chosenOption.text + '\u201D</p>' +
                    '</div>' +
                '</div>';
        }

        root.innerHTML =
            '<div class="game-arena' + (state.chaosMode ? ' game-arena--chaos' : '') + '">' +
                statsHTML + roundHTML + mgrHTML + promptHTML + optionsHTML +
            '</div>';

        // Bind clicks
        if (!state.animating && round) {
            var btns = root.querySelectorAll('.game-option');
            for (var j = 0; j < btns.length; j++) {
                btns[j].addEventListener('click', handleOptionClick);
            }
        }
    }

    // ==================== SCREEN: ESCALATION ====================

    function renderEscalation() {
        var esc = state.escalation;
        if (!esc) return;

        var effectsHTML = '<div class="game-escalation__effects">';
        var keys = ['ME', 'MP', 'TM', 'DR'];
        for (var i = 0; i < keys.length; i++) {
            var k = keys[i];
            var v = esc.effects[k];
            if (v) {
                var cls = v > 0 ? 'game-escalation__val--pos' : 'game-escalation__val--neg';
                effectsHTML += '<span class="game-escalation__val ' + cls + '">' +
                    statLabel(k) + ' ' + (v > 0 ? '+' : '') + v + '</span>';
            }
        }
        effectsHTML += '</div>';

        root.innerHTML =
            '<div class="game-escalation-overlay">' +
                '<div class="game-escalation">' +
                    '<div class="game-escalation__icon">\u26A0\uFE0F</div>' +
                    '<h2 class="game-escalation__title">ESCALATION EVENT</h2>' +
                    '<p class="game-escalation__text">' + esc.text + '</p>' +
                    effectsHTML +
                    '<button class="game-btn game-btn--esc" id="game-esc-ok">CONTINUE</button>' +
                '</div>' +
            '</div>';

        document.getElementById('game-esc-ok').addEventListener('click', function () {
            applyEffects(esc.effects);
            state.escalation = null;

            var end = checkWinLoss();
            if (end) {
                state.endCondition = end;
                state.screen = 'result';
                render();
            } else {
                state.screen = 'battle';
                nextTurn();
            }
        });
    }

    // ==================== SCREEN: CHAOS ====================

    function renderChaosEvent() {
        root.innerHTML =
            '<div class="game-escalation-overlay game-escalation-overlay--chaos">' +
                '<div class="game-escalation game-escalation--chaos">' +
                    '<div class="game-escalation__icon">\uD83D\uDD25</div>' +
                    '<h2 class="game-escalation__title game-escalation__title--chaos">CHAOS MODE ACTIVATED</h2>' +
                    '<p class="game-escalation__text">Manager Patience has collapsed! All negative effects on your stats are now doubled.</p>' +
                    '<button class="game-btn game-btn--esc" id="game-chaos-ok">BRACE YOURSELF</button>' +
                '</div>' +
            '</div>';

        document.getElementById('game-chaos-ok').addEventListener('click', function () {
            state.screen = 'battle';
            render();
        });
    }

    // ==================== SCREEN: RESULT ====================

    function renderResult() {
        var end = state.endCondition;
        var devWon = end ? end.winner === 'dev' : state.DR > state.TM;
        var grade = getGrade();
        var narrative = getGradeNarrative(grade);

        var headingText, headingCls;
        if (end) {
            headingText = devWon ? 'YOU WIN!' : 'YOU LOSE!';
            headingCls = devWon ? ' game-result__heading--win' : ' game-result__heading--lose';
        } else {
            // Time's up - grade-based
            headingText = grade === 'F' ? 'YOU LOSE!' : 'TIME\'S UP!';
            headingCls = grade === 'F' ? ' game-result__heading--lose' : '';
        }

        var reasonHTML = end ? '<p class="game-result__reason">' + end.reason + '</p>' : '';

        root.innerHTML =
            '<div class="game-result">' +
                '<h1 class="game-result__heading' + headingCls + '">' + headingText + '</h1>' +
                reasonHTML +
                '<div class="game-result__sprites">' +
                    buildSVG(DEV_SPRITE, 3) +
                    '<span class="game-result__vs">vs</span>' +
                    buildSVG(MGR_SPRITE, 3) +
                '</div>' +
                '<div class="game-result__grade">Grade: <strong>' + grade + '</strong></div>' +
                '<p class="game-result__narrative">' + narrative + '</p>' +
                '<div class="game-result__stats">' +
                    statBarHTML('ME', state.ME) +
                    statBarHTML('MP', state.MP) +
                    statBarHTML('TM', state.TM) +
                    statBarHTML('DR', state.DR) +
                '</div>' +
                '<button class="game-btn game-btn--start" id="game-restart">PLAY AGAIN</button>' +
            '</div>';

        document.getElementById('game-restart').addEventListener('click', startGame);
    }

    // ==================== ROUTER ====================

    function render() {
        switch (state.screen) {
            case 'title':      return renderTitle();
            case 'battle':     return renderBattle();
            case 'escalation': return renderEscalation();
            case 'chaos':      return renderChaosEvent();
            case 'result':     return renderResult();
        }
    }

    // ==================== GAME LOGIC ====================

    function startGame() {
        state.screen = 'battle';
        state.ME = 60;
        state.MP = 70;
        state.TM = 75;
        state.DR = 50;
        state.round = 0;
        state.deck = shuffle(ROUNDS).slice(0, 10);
        state.currentRound = null;
        state.chosenOption = null;
        state.reaction = '';
        state.reactionTier = '';
        state.escalation = null;
        state.chaosMode = false;
        state.chaosTriggered = false;
        state.animating = false;
        state.statChanges = null;
        state.endCondition = null;
        state.seniorCardAvailable = false;
        state.seniorCardUsed = false;

        nextTurn();
    }

    function nextTurn() {
        if (state.deck.length === 0 || state.round >= state.maxRounds) {
            state.screen = 'result';
            render();
            return;
        }

        state.round++;
        state.currentRound = state.deck.pop();
        state.chosenOption = null;
        state.animating = false;
        state.reaction = '';
        state.reactionTier = '';
        state.statChanges = null;

        checkSeniorCard();
        render();
    }

    function handleOptionClick() {
        if (state.animating) return;
        var idx = parseInt(this.getAttribute('data-index'), 10);
        playOption(idx);
    }

    function playOption(idx) {
        if (state.animating || !state.currentRound) return;

        var option = state.currentRound.options[idx];
        state.chosenOption = option;
        state.animating = true;

        // Calculate and apply effects
        var changes = calculateEffects(option);
        state.statChanges = changes;
        applyEffects(changes);

        // Get manager reaction
        var tier = getReactionTier(state.ME);
        state.reactionTier = tier;
        state.reaction = randomFrom(REACTIONS[tier]);

        render();

        // After animation delay, check conditions and proceed
        setTimeout(function () {
            state.animating = false;
            state.statChanges = null;
            state.reaction = '';

            // Check win/loss
            var end = checkWinLoss();
            if (end) {
                state.endCondition = end;
                state.screen = 'result';
                render();
                return;
            }

            // Check chaos mode
            if (checkChaos()) {
                state.screen = 'chaos';
                render();
                return;
            }

            // Check escalation (every 3 rounds)
            if (state.round % 3 === 0 && state.round < state.maxRounds) {
                state.escalation = randomFrom(ESCALATIONS);
                state.screen = 'escalation';
                render();
                return;
            }

            nextTurn();
        }, 2500);
    }

    // ==================== INIT ====================
    render();
})();
